import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Mail, MessageSquare, CheckCircle2, AlertCircle, Eye, Pencil, RotateCcw,
  ChevronLeft, Lock, Loader2, ChevronDown, ChevronUp, ExternalLink,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface CommSettings {
  email_configured: boolean;
  sms_configured: boolean;
  sendgrid_from_email: string | null;
  sendgrid_from_name: string | null;
  sendgrid_api_key_masked: string | null;
  twilio_from_number: string | null;
  twilio_account_sid_masked: string | null;
  twilio_auth_token_masked: string | null;
  updated_at: string | null;
}

interface TierCapabilities {
  tier_name: string;
  display_name: string;
  can_edit_template_body: boolean;
  can_white_label: boolean;
}

interface TemplateItem {
  template_key: string;
  pipeline_stage: string | null;
  system_subject: string;
  system_body_html: string;
  footer_html: string | null;
  custom_subject: string | null;
  custom_body_html: string | null;
  has_override: boolean;
}

const STAGE_LABELS: Record<string, string> = {
  assessment_invitation: "Assessment Invitation",
  assessment_reminder: "Assessment Reminder (48hr)",
  submission_confirmed: "Submission Confirmed",
  outcome_progressing: "Outcome — Progressing",
  outcome_not_proceeding: "Outcome — Not Proceeding",
};

export default function SettingsCommunications() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [settings, setSettings] = useState<CommSettings | null>(null);
  const [tier, setTier] = useState<TierCapabilities | null>(null);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [emailKey, setEmailKey] = useState("");
  const [emailFrom, setEmailFrom] = useState("");
  const [emailName, setEmailName] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  const [twilioSid, setTwilioSid] = useState("");
  const [twilioToken, setTwilioToken] = useState("");
  const [twilioFrom, setTwilioFrom] = useState("");
  const [smsSaving, setSmsSaving] = useState(false);

  const [testEmailAddr, setTestEmailAddr] = useState("");
  const [testSmsNum, setTestSmsNum] = useState("");
  const [testingSms, setTestingSms] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const [guideCollapsed, setGuideCollapsed] = useState(false);

  const [previewTemplate, setPreviewTemplate] = useState<TemplateItem | null>(null);
  const [editTemplate, setEditTemplate] = useState<TemplateItem | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  async function loadData() {
    setLoading(true);
    const [settingsRes, tierRes, tplRes] = await Promise.all([
      fetch("/api/workspace/communication-settings").then((r) => r.json()).catch(() => null),
      fetch("/api/workspace/communication-settings/tier-capabilities").then((r) => r.json()).catch(() => null),
      fetch("/api/workspace/communication-settings/templates").then((r) => r.json()).catch(() => []),
    ]);
    setSettings(settingsRes);
    setTier(tierRes);
    setTemplates(Array.isArray(tplRes) ? tplRes : []);
    if (settingsRes?.sendgrid_from_email) setEmailFrom(settingsRes.sendgrid_from_email);
    if (settingsRes?.sendgrid_from_name) setEmailName(settingsRes.sendgrid_from_name);
    if (settingsRes?.twilio_from_number) setTwilioFrom(settingsRes.twilio_from_number);
    if (settingsRes?.sendgrid_api_key_masked) setGuideCollapsed(true);
    setLoading(false);
  }

  useEffect(() => { loadData(); }, []);

  async function saveEmail() {
    if (!emailKey && !settings?.sendgrid_api_key_masked) {
      toast({ title: "API key required", description: "Enter your SendGrid API key.", variant: "destructive" });
      return;
    }
    if (!emailFrom) {
      toast({ title: "From email required", description: "Enter the sender email address.", variant: "destructive" });
      return;
    }
    setEmailSaving(true);
    const body: any = { sendgrid_from_email: emailFrom, sendgrid_from_name: emailName };
    if (emailKey) body.sendgrid_api_key = emailKey;
    const res = await apiRequest("POST", "/api/workspace/communication-settings/email", body);
    setEmailSaving(false);
    if (res.ok) {
      toast({ title: "Email settings saved" });
      setEmailKey("");
      loadData();
    } else {
      const data = await res.json().catch(() => ({}));
      toast({ title: "Failed to save", description: data.error || "An error occurred", variant: "destructive" });
    }
  }

  async function testEmail() {
    if (!testEmailAddr) {
      toast({ title: "Enter a test email address", variant: "destructive" });
      return;
    }
    setTestingEmail(true);
    const res = await apiRequest("POST", "/api/workspace/communication-settings/test-email", { to: testEmailAddr });
    setTestingEmail(false);
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast({ title: "Test email sent", description: `Sent to ${testEmailAddr}` });
      setGuideCollapsed(true);
    } else {
      toast({ title: "Test failed", description: data.error || "An error occurred", variant: "destructive" });
    }
  }

  async function saveSms() {
    if (!twilioSid && !settings?.twilio_account_sid_masked) {
      toast({ title: "Account SID required", variant: "destructive" });
      return;
    }
    if (!twilioToken && !settings?.twilio_auth_token_masked) {
      toast({ title: "Auth token required", variant: "destructive" });
      return;
    }
    if (!twilioFrom) {
      toast({ title: "From number required", variant: "destructive" });
      return;
    }
    setSmsSaving(true);
    const body: any = { twilio_from_number: twilioFrom };
    if (twilioSid) body.twilio_account_sid = twilioSid;
    if (twilioToken) body.twilio_auth_token = twilioToken;
    const res = await apiRequest("POST", "/api/workspace/communication-settings/sms", body);
    setSmsSaving(false);
    if (res.ok) {
      toast({ title: "SMS settings saved" });
      setTwilioSid("");
      setTwilioToken("");
      loadData();
    } else {
      const data = await res.json().catch(() => ({}));
      toast({ title: "Failed to save", description: data.error || "An error occurred", variant: "destructive" });
    }
  }

  async function testSms() {
    if (!testSmsNum) {
      toast({ title: "Enter a test phone number", variant: "destructive" });
      return;
    }
    setTestingSms(true);
    const res = await apiRequest("POST", "/api/workspace/communication-settings/test-sms", { to: testSmsNum });
    setTestingSms(false);
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast({ title: "Test SMS sent", description: `Sent to ${testSmsNum}` });
    } else {
      toast({ title: "Test failed", description: data.error || "An error occurred", variant: "destructive" });
    }
  }

  function openEdit(tpl: TemplateItem) {
    setEditTemplate(tpl);
    setEditSubject(tpl.custom_subject ?? tpl.system_subject ?? "");
    setEditBody(tpl.custom_body_html ?? tpl.system_body_html ?? "");
  }

  async function saveTemplateOverride() {
    if (!editTemplate) return;
    setEditSaving(true);
    const canEditSubject = tier?.can_white_label;
    const body: any = { custom_body_html: editBody };
    if (canEditSubject) body.custom_subject = editSubject;
    const res = await apiRequest("PUT", `/api/workspace/communication-settings/templates/${editTemplate.template_key}`, body);
    setEditSaving(false);
    if (res.ok) {
      toast({ title: "Template saved" });
      setEditTemplate(null);
      loadData();
    } else {
      const data = await res.json().catch(() => ({}));
      toast({ title: "Failed to save template", description: data.error || "An error occurred", variant: "destructive" });
    }
  }

  async function resetTemplateOverride(templateKey: string) {
    const res = await apiRequest("DELETE", `/api/workspace/communication-settings/templates/${templateKey}/override`);
    if (res.ok) {
      toast({ title: "Template reset to default" });
      loadData();
    } else {
      toast({ title: "Reset failed", variant: "destructive" });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const canEditBody = tier?.can_edit_template_body ?? false;
  const canEditSubject = tier?.can_white_label ?? false;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/agent/dashboard")}
          className="text-muted-foreground hover:text-foreground"
          data-testid="communications-back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Communications</h1>
          <p className="text-sm text-muted-foreground">
            Manage outbound email and SMS settings for your workspace
          </p>
        </div>
      </div>

      {/* SendGrid Setup Guide */}
      <Card className="overflow-hidden" data-testid="comms-setup-guide">
        <button
          onClick={() => setGuideCollapsed(c => !c)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/30 transition-colors"
          data-testid="button-toggle-guide"
        >
          <span className="font-semibold text-sm flex items-center gap-2">
            <Mail className="w-4 h-4 text-indigo-400" />
            How to set up your sending email — takes about 2 minutes
          </span>
          {guideCollapsed
            ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
            : <ChevronUp className="w-4 h-4 text-muted-foreground" />
          }
        </button>

        {!guideCollapsed && (
          <div className="px-6 pb-6 space-y-4 border-t border-border">
            <div className="grid gap-3 pt-4">
              {[
                {
                  step: 1,
                  title: "Create a free SendGrid account",
                  detail: "sendgrid.com/free",
                  href: "https://sendgrid.com/free",
                },
                {
                  step: 2,
                  title: "Go to Settings → API Keys → Create API Key",
                  detail: 'Select "Full Access" and copy the key',
                  href: null,
                },
                {
                  step: 3,
                  title: "Paste the API key into the field below",
                  detail: null,
                  href: null,
                },
                {
                  step: 4,
                  title: "Enter your sending email address",
                  detail: "This must be a verified sender in your SendGrid account",
                  href: null,
                },
                {
                  step: 5,
                  title: "Click Save, then use the Test button to confirm",
                  detail: null,
                  href: null,
                },
              ].map(({ step, title, detail, href }) => (
                <div key={step} className="flex gap-3 items-start">
                  <div className="w-6 h-6 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {step}
                  </div>
                  <div>
                    <p className="text-sm font-medium leading-snug">{title}</p>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 mt-0.5"
                      >
                        {detail}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : detail ? (
                      <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              Need help? Email{" "}
              <a href="mailto:support@vexistalentagency.com" className="text-indigo-400 hover:text-indigo-300">
                support@vexistalentagency.com
              </a>
            </p>
          </div>
        )}
      </Card>

      {/* Email Section */}
      <Card className="p-6 space-y-5" data-testid="comms-email-card">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </h2>
          {settings?.email_configured ? (
            <Badge className="bg-green-500/15 text-green-600 border-green-500/30 text-xs">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Configured
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/40">
              <AlertCircle className="w-3 h-3 mr-1" />
              Not configured
            </Badge>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email-api-key" className="text-sm">
              SendGrid API Key
            </Label>
            <Input
              id="email-api-key"
              type="password"
              placeholder={settings?.sendgrid_api_key_masked ?? "SG.xxxxxxxxxx..."}
              value={emailKey}
              onChange={(e) => setEmailKey(e.target.value)}
              data-testid="input-sendgrid-api-key"
            />
            <p className="text-xs text-muted-foreground">
              {settings?.sendgrid_api_key_masked
                ? `Current: ${settings.sendgrid_api_key_masked} — leave blank to keep`
                : "Enter your SendGrid API key starting with SG."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="email-from" className="text-sm">
                From Email
              </Label>
              <Input
                id="email-from"
                type="email"
                placeholder="noreply@yourdomain.com"
                value={emailFrom}
                onChange={(e) => setEmailFrom(e.target.value)}
                data-testid="input-sendgrid-from-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email-name" className="text-sm">
                From Name
              </Label>
              <Input
                id="email-name"
                placeholder="Vexis"
                value={emailName}
                onChange={(e) => setEmailName(e.target.value)}
                data-testid="input-sendgrid-from-name"
              />
            </div>
          </div>

          <Button
            onClick={saveEmail}
            disabled={emailSaving}
            size="sm"
            data-testid="button-save-email"
          >
            {emailSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save email settings
          </Button>
        </div>

        {settings?.email_configured && (
          <div className="border-t border-border pt-4">
            <p className="text-sm font-medium mb-3">Send a test email</p>
            <div className="flex gap-2">
              <Input
                placeholder="recipient@example.com"
                value={testEmailAddr}
                onChange={(e) => setTestEmailAddr(e.target.value)}
                className="max-w-xs"
                data-testid="input-test-email"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={testEmail}
                disabled={testingEmail}
                data-testid="button-test-email"
              >
                {testingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send test
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* SMS Section */}
      <Card className="p-6 space-y-5" data-testid="comms-sms-card">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            SMS
          </h2>
          {settings?.sms_configured ? (
            <Badge className="bg-green-500/15 text-green-600 border-green-500/30 text-xs">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Configured
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              Not configured
            </Badge>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="twilio-sid" className="text-sm">
              Account SID
            </Label>
            <Input
              id="twilio-sid"
              type="password"
              placeholder={settings?.twilio_account_sid_masked ?? "ACxxxxxxxxxx..."}
              value={twilioSid}
              onChange={(e) => setTwilioSid(e.target.value)}
              data-testid="input-twilio-account-sid"
            />
            {settings?.twilio_account_sid_masked && (
              <p className="text-xs text-muted-foreground">
                Current: {settings.twilio_account_sid_masked} — leave blank to keep
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="twilio-token" className="text-sm">
              Auth Token
            </Label>
            <Input
              id="twilio-token"
              type="password"
              placeholder={settings?.twilio_auth_token_masked ?? "xxxxxxxxxx..."}
              value={twilioToken}
              onChange={(e) => setTwilioToken(e.target.value)}
              data-testid="input-twilio-auth-token"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="twilio-from" className="text-sm">
              From Number
            </Label>
            <Input
              id="twilio-from"
              placeholder="+61400000000"
              value={twilioFrom}
              onChange={(e) => setTwilioFrom(e.target.value)}
              data-testid="input-twilio-from-number"
            />
          </div>

          <Button
            onClick={saveSms}
            disabled={smsSaving}
            size="sm"
            data-testid="button-save-sms"
          >
            {smsSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save SMS settings
          </Button>
        </div>

        {settings?.sms_configured && (
          <div className="border-t border-border pt-4">
            <p className="text-sm font-medium mb-3">Send a test SMS</p>
            <div className="flex gap-2">
              <Input
                placeholder="+61400000000"
                value={testSmsNum}
                onChange={(e) => setTestSmsNum(e.target.value)}
                className="max-w-xs"
                data-testid="input-test-sms"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={testSms}
                disabled={testingSms}
                data-testid="button-test-sms"
              >
                {testingSms ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send test
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Email Templates Section */}
      <Card className="p-6" data-testid="comms-templates-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Templates
          </h2>
          {!canEditBody && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="w-3 h-3" />
              <span>
                Template editing requires{" "}
                <span className="font-medium text-foreground">Growth</span> or above
              </span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {templates.map((tpl) => (
            <div
              key={tpl.template_key}
              className="flex items-center justify-between py-3 border-b border-border last:border-0"
              data-testid={`template-row-${tpl.template_key}`}
            >
              <div>
                <p className="text-sm font-medium">
                  {STAGE_LABELS[tpl.template_key] || tpl.template_key}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {tpl.custom_subject ?? tpl.system_subject}
                </p>
                {tpl.has_override && (
                  <Badge
                    variant="outline"
                    className="mt-1 text-xs h-4 border-violet-500/30 text-violet-600"
                  >
                    Custom
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewTemplate(tpl)}
                  className="h-7 text-xs gap-1"
                  data-testid={`button-preview-${tpl.template_key}`}
                >
                  <Eye className="w-3 h-3" />
                  Preview
                </Button>
                {canEditBody && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(tpl)}
                      className="h-7 text-xs gap-1"
                      data-testid={`button-edit-${tpl.template_key}`}
                    >
                      <Pencil className="w-3 h-3" />
                      Edit
                    </Button>
                    {tpl.has_override && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => resetTemplateOverride(tpl.template_key)}
                        className="h-7 text-xs gap-1 text-muted-foreground"
                        data-testid={`button-reset-${tpl.template_key}`}
                      >
                        <RotateCcw className="w-3 h-3" />
                        Reset
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No templates found
            </p>
          )}
        </div>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={Boolean(previewTemplate)} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle>
              {previewTemplate ? (STAGE_LABELS[previewTemplate.template_key] || previewTemplate.template_key) : ""}
            </DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase">Subject</p>
                <p className="text-sm border border-border rounded px-3 py-2 bg-muted/30">
                  {previewTemplate.custom_subject ?? previewTemplate.system_subject}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase">Body</p>
                <div
                  className="text-sm border border-border rounded px-3 py-3 bg-muted/30 prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{
                    __html: previewTemplate.custom_body_html ?? previewTemplate.system_body_html,
                  }}
                />
              </div>
              {previewTemplate.footer_html && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Footer</p>
                  <div
                    className="text-xs border border-border rounded px-3 py-2 bg-muted/20 text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: previewTemplate.footer_html }}
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={Boolean(editTemplate)} onOpenChange={() => setEditTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              Edit: {editTemplate ? (STAGE_LABELS[editTemplate.template_key] || editTemplate.template_key) : ""}
            </DialogTitle>
          </DialogHeader>
          {editTemplate && (
            <div className="space-y-4">
              {canEditSubject && (
                <div className="space-y-1.5">
                  <Label className="text-sm">Subject line</Label>
                  <Input
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    data-testid="input-edit-subject"
                  />
                </div>
              )}
              {!canEditSubject && (
                <div className="space-y-1.5">
                  <Label className="text-sm flex items-center gap-1.5">
                    Subject line
                    <Lock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-normal">Enterprise only</span>
                  </Label>
                  <Input value={editTemplate.system_subject} disabled className="opacity-60" />
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-sm">Body (HTML)</Label>
                <Textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={12}
                  className="font-mono text-xs"
                  data-testid="input-edit-body"
                />
                <p className="text-xs text-muted-foreground">
                  Merge tags available: {'{{candidate_first_name}}'}, {'{{role_title}}'}, {'{{company_name}}'},{" "}
                  {'{{recruiter_name}}'}, {'{{assessment_url}}'}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditTemplate(null)}
              data-testid="button-cancel-edit"
            >
              Cancel
            </Button>
            <Button onClick={saveTemplateOverride} disabled={editSaving} data-testid="button-save-template">
              {editSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
